import { Component, type ReactNode } from "react";

import { Button } from "@/design-system/Button";

type Props = {
  readonly children: ReactNode;
  /** A new value clears a caught error, e.g. when the reader moves to another page. */
  readonly resetKey?: string;
};
type State = { readonly error: Error | null };

/**
 * Keeps one failing page from blanking the whole application.
 *
 * Without it a single render error unmounts everything React drew and leaves
 * an empty screen that says nothing about what broke. Here the menu stays, the
 * reader sees which error stopped the page, and can reload it.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: unknown): State {
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  override componentDidUpdate(previous: Props): void {
    if (previous.resetKey !== this.props.resetKey && this.state.error !== null) {
      this.setState({ error: null });
    }
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;
    return (
      <div role="alert" data-testid="page-error" className="flex flex-col gap-3 rounded-panel border border-status-warning p-4 text-body-main text-text-secondary">
        <p className="text-text-primary">Этот раздел не смог отобразиться. Остальной сайт работает.</p>
        <p className="font-mono text-helper-sm [overflow-wrap:anywhere]">{error.message}</p>
        <div>
          <Button type="button" onClick={() => { window.location.reload(); }}>Обновить страницу</Button>
        </div>
      </div>
    );
  }
}
